/**
 * BATCH 23 PART 18 — Data, AI, Knowledge & Research Templates 281–298
 * Services: HubSpot, Clearbit, Gong, Pinecone, Zendesk, Google Sheets, Google Docs, Ahrefs, Airtable, Notion, Slack, OpenAI
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "HubSpot: Fetch contact profile", not "API Call")
 * 7. Each workflow has 8 nodes with proper edges
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 23 PART 18 — TEMPLATES 281–298 (Data, AI, Knowledge & Research)
// =============================================================================

export const ELITE_BATCH23_PART18: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 281. AI CHAT FOR LEAD GENERATION — Pattern K (Lead Scoring/Routing)
  // ===================================================================

  "AI Chat for Lead Generation": {
    nodes: [
      trigger(
        "Webhook: Chat message received from website visitor",
        "webhook_app",
        {
          source: "Website Chat",
          event: "chat.message_received",
          howItStarts:
            "A webhook fires when a website visitor sends a message through the chat widget, initiating an AI-powered lead qualification conversation",
          setup:
            "Connect your website chat widget to POST incoming messages to this workflow URL. Include visitor ID, message text, page URL, and any UTM parameters.",
          inputFields: [],
        },
      ),

      // 2. AI: Classify lead intent
      aiNode(
        "2",
        "AI: Classify visitor intent and extract lead signals from chat message",
        240,
        "You are a lead qualification specialist. Analyze the chat message and classify the visitor_intent (product_inquiry, pricing_request, support_question, partnership, job_inquiry, spam), extract visitor_name if mentioned, visitor_email if shared, company_name if mentioned, product_interest, urgency (low, medium, high), and is_qualified (boolean: true if product/pricing intent with business context). Return JSON.",
        "Classify lead intent from this chat message: {webhook_payload}",
        [
          { field: "visitor_intent", as: "visitor_intent", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "product_interest", as: "product_interest", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "is_qualified", as: "is_qualified", type: "boolean" },
        ],
      ),

      // 3. HubSpot: Lookup existing contact
      n(
        "3",
        "hitApi",
        "HubSpot: Search for existing contact by email or company name",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{visitor_email}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["firstname","lastname","email","company","lifecyclestage","hs_lead_status"]',
              },
            ],
            variables: [
              { name: "total", customName: "contact_matches", type: "number" },
              {
                name: "results[0].id",
                customName: "hubspot_contact_id",
                type: "string",
              },
              {
                name: "results[0].properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. AI: Generate qualifying questions
      aiNode(
        "4",
        "AI: Generate personalized qualifying questions based on visitor context",
        720,
        "You are a conversational sales AI. Based on the visitor intent, product interest, and any known company context, generate 2-3 targeted qualifying questions to determine budget, timeline, and decision authority. Also generate a helpful_response that addresses their initial question. Return JSON with qualifying_questions (array of strings) and helpful_response (string).",
        "Generate qualifying questions for: Intent={visitor_intent}, Product={product_interest}, Company={company_name}, Urgency={urgency}, Existing contact={contact_matches > 0}",
        [
          {
            field: "qualifying_questions",
            as: "qualifying_questions",
            type: "string",
          },
          { field: "helpful_response", as: "helpful_response", type: "string" },
        ],
      ),

      // 5. Condition: Is the visitor a qualified lead?
      n(
        "5",
        "condition",
        "Is the visitor a qualified lead based on intent and business context?",
        960,
        {
          conditionData: [
            {
              variable: "is_qualified",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. HubSpot: Create lead
      n(
        "6",
        "hitApi",
        "HubSpot: Create or update lead with chat qualification data",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{visitor_email}","firstname":"{visitor_name}","company":"{company_name}","hs_lead_status":"NEW","lifecyclestage":"lead","notes_last_contacted":"Chat lead: {visitor_intent}. Product interest: {product_interest}. Urgency: {urgency}."}',
              },
            ],
            variables: [
              { name: "id", customName: "new_contact_id", type: "string" },
              {
                name: "properties.lifecyclestage",
                customName: "new_lifecycle",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Slack: Alert SDR
      slackNode(
        "7",
        "Slack: Alert SDR about new qualified chat lead with context",
        1440,
        "New qualified chat lead: {visitor_name} from {company_name}. Intent: {visitor_intent}. Product: {product_interest}. Urgency: {urgency}. HubSpot: #{new_contact_id}. Qualifying Qs to ask: {qualifying_questions}",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log chat lead interaction and qualification status",
        1680,
        "ChatLeads",
        '[["{ visitor_name }","{ visitor_email }","{ company_name }","{ visitor_intent }","{ product_interest }","{ urgency }","{ is_qualified }","{ new_contact_id }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Qualified -> create lead in HubSpot
      e("5", "8", "output_2"), // Not qualified -> log only
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Qualifies website chat visitors as leads using AI. When a visitor sends a chat message, AI classifies their intent and urgency, HubSpot checks for existing contacts, and AI generates personalized qualifying questions. Qualified leads are created in HubSpot and an SDR is alerted via Slack with full context, while all interactions are logged to Google Sheets.",
  },

  // ===================================================================
  // 282. AI EMAIL ASSISTANT — Pattern I (Content/Event Nurture)
  // ===================================================================

  "AI Email Assistant": {
    nodes: [
      trigger("Email: Compose request received in shared inbox", "email", {
        source: "Email",
        event: "email.compose_request",
        howItStarts:
          "An email arrives in a shared inbox with a compose request, triggering AI-assisted drafting using CRM context and contact history",
        setup:
          "Configure email forwarding so compose requests (subject starting with 'Draft:' or 'Compose:') are forwarded to this workflow trigger address.",
        inputFields: [],
      }),

      // 2. AI: Extract context from compose request
      aiNode(
        "2",
        "AI: Extract email compose context including recipient and purpose",
        240,
        "You are an email assistant. Extract the sender_email (person requesting the draft), recipient_email, recipient_name, email_purpose (follow_up, introduction, proposal, thank_you, scheduling, check_in), key_points (array of things to mention), tone (formal, friendly, professional), and urgency from the compose request. Return JSON.",
        "Extract compose request details from: {webhook_payload}",
        [
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "email_purpose", as: "email_purpose", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "tone", as: "tone", type: "string" },
        ],
      ),

      // 3. HubSpot: Fetch contact history
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch recipient contact profile and recent interaction history",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{recipient_email}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["firstname","lastname","company","jobtitle","notes_last_contacted","hs_lead_status","recent_deal_amount"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.firstname",
                customName: "contact_first_name",
                type: "string",
              },
              {
                name: "results[0].properties.company",
                customName: "contact_company",
                type: "string",
              },
              {
                name: "results[0].properties.jobtitle",
                customName: "contact_title",
                type: "string",
              },
              {
                name: "results[0].properties.notes_last_contacted",
                customName: "last_interaction",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. AI: Draft email using CRM context
      aiNode(
        "4",
        "AI: Draft personalized email using CRM context and contact history",
        720,
        "You are a professional email writer. Draft an email for the given purpose using CRM context. Include a subject_line and email_body. Reference the contact's company, role, and last interaction when relevant. Match the requested tone. Be concise but thorough. Flag if the content might be sensitive (contains pricing, legal terms, or personnel matters). Return JSON with subject_line, email_body, is_sensitive (boolean).",
        "Draft email: Purpose={email_purpose}, To={recipient_name} ({contact_title} at {contact_company}), Key points={key_points}, Tone={tone}, Last interaction={last_interaction}",
        [
          { field: "subject_line", as: "draft_subject", type: "string" },
          { field: "email_body", as: "draft_body", type: "string" },
          { field: "is_sensitive", as: "is_sensitive", type: "boolean" },
        ],
      ),

      // 5. Condition: Contains sensitive content?
      n(
        "5",
        "condition",
        "Does the draft contain sensitive content requiring manual review?",
        960,
        {
          conditionData: [
            {
              variable: "is_sensitive",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Approval gate: Sender review
      n(
        "6",
        "approvalGate",
        "Sender: Review and approve AI-drafted email before sending",
        1200,
        {
          approvalGateDetails: {
            approverEmail: "{sender_email}",
            message:
              "AI drafted this email to {recipient_name} ({recipient_email}). Subject: {draft_subject}. Please review the content before it is sent. This was flagged as containing sensitive content.",
          },
        },
      ),

      // 7. Email: Send the drafted email
      emailNode(
        "7",
        "Email: Send the approved AI-drafted email to the recipient",
        1440,
        "{recipient_email}",
        "{draft_subject}",
        "{draft_body}",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log AI email draft activity and send status",
        1680,
        "EmailAssistant",
        '[["{ sender_email }","{ recipient_email }","{ email_purpose }","{ draft_subject }","{ is_sensitive }","{ tone }","sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Sensitive -> require approval
      e("5", "7", "output_2"), // Not sensitive -> send directly
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "AI-powered email assistant that drafts context-aware emails using CRM data. When a compose request arrives, AI extracts the purpose and recipient, HubSpot provides contact history and company context, and AI drafts a personalized email matching the requested tone. Sensitive content is routed through sender approval before sending, and all drafts are logged to Google Sheets.",
  },

  // ===================================================================
  // 283. MEETING PREP AGENT — Pattern K (Lead Scoring/Routing)
  // ===================================================================

  "Meeting Prep Agent": {
    nodes: [
      trigger("Webhook: Calendar event starting in 2 hours", "webhook_app", {
        source: "Google Calendar",
        event: "calendar.event_reminder",
        howItStarts:
          "Google Calendar fires a webhook 2 hours before a meeting, triggering automated research and prep brief generation for all attendees",
        setup:
          "Configure Google Calendar notifications to POST event data 2 hours before meetings to this workflow URL. Include attendee emails, event title, and description.",
        inputFields: [],
      }),

      // 2. HubSpot: Fetch attendee data
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch contact profiles for all meeting attendees",
        240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"IN","values":{attendee_emails}}]}]',
              },
              {
                key: "properties",
                value:
                  '["firstname","lastname","email","company","jobtitle","notes_last_contacted","hs_lead_status","recent_deal_amount","num_associated_deals"]',
              },
            ],
            variables: [
              { name: "total", customName: "attendee_count", type: "number" },
              {
                name: "results",
                customName: "attendee_profiles",
                type: "array",
              },
            ],
          },
        },
      ),

      // 3. Clearbit: Enrich company data
      n(
        "3",
        "hitApi",
        "Clearbit: Enrich company data for primary attendee organization",
        480,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={attendee_domain}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              { name: "name", customName: "company_name", type: "string" },
              {
                name: "metrics.employees",
                customName: "company_size",
                type: "number",
              },
              {
                name: "metrics.estimatedAnnualRevenue",
                customName: "company_revenue",
                type: "string",
              },
              {
                name: "category.industry",
                customName: "company_industry",
                type: "string",
              },
              { name: "tech", customName: "tech_stack", type: "array" },
            ],
          },
        },
      ),

      // 4. AI: Generate meeting prep brief
      aiNode(
        "4",
        "AI: Generate comprehensive meeting prep brief with talking points",
        720,
        "You are a meeting preparation analyst. Generate a comprehensive meeting prep brief including: executive_summary (2-3 sentences about who you are meeting and why), attendee_bios (role, tenure, LinkedIn-style summary for each), company_overview (size, revenue, industry, recent news), relationship_history (based on CRM notes and deal status), talking_points (5 bullet points), potential_objections (3 likely concerns), and recommended_ask (specific next step to propose). Return JSON.",
        "Generate meeting prep for: Event={meeting_title}, Attendees={attendee_profiles}, Company={company_name} ({company_industry}, {company_size} employees, {company_revenue} revenue), Tech stack={tech_stack}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          {
            field: "potential_objections",
            as: "potential_objections",
            type: "string",
          },
          { field: "recommended_ask", as: "recommended_ask", type: "string" },
          { field: "company_overview", as: "company_overview", type: "string" },
        ],
      ),

      // 5. Google Docs: Create prep brief document
      n("5", "hitApi", "Google Docs: Create meeting prep brief document", 960, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "Meeting Prep: {meeting_title} - {company_name}",
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "revisionId", customName: "doc_revision", type: "string" },
          ],
        },
      }),

      // 6. Email: Send prep brief to meeting host
      emailNode(
        "6",
        "Email: Send meeting prep brief to host 90 minutes before meeting",
        1200,
        "{host_email}",
        "Meeting Prep: {meeting_title} with {company_name} in 2 hours",
        "<h2>Meeting Prep Brief</h2><h3>Executive Summary</h3><p>{executive_summary}</p><h3>Company Overview</h3><p>{company_overview}</p><h3>Talking Points</h3><p>{talking_points}</p><h3>Potential Objections</h3><p>{potential_objections}</p><h3>Recommended Ask</h3><p>{recommended_ask}</p><p><a href='https://docs.google.com/document/d/{doc_id}'>Full Brief in Google Docs</a></p>",
      ),

      // 7. Slack: Send reminder with brief link
      slackNode(
        "7",
        "Slack: Send meeting reminder with prep brief link to attendee",
        1440,
        "Meeting in 2 hours: {meeting_title} with {company_name}. {executive_summary} Prep brief: https://docs.google.com/document/d/{doc_id}. Recommended ask: {recommended_ask}",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log meeting prep generation for tracking",
        1680,
        "MeetingPrep",
        '[["{ meeting_title }","{ company_name }","{ attendee_count }","{ company_industry }","{ company_size }","{ doc_id }","prepared"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically prepares meeting briefs 2 hours before scheduled meetings. When a calendar reminder fires, HubSpot fetches attendee profiles, Clearbit enriches company data, and AI generates a comprehensive prep brief with talking points, potential objections, and a recommended ask. The brief is saved to Google Docs, emailed to the host, and shared via Slack.",
  },

  // ===================================================================
  // 284. CALL ANALYSIS AGENT — Pattern K (Lead Scoring/Routing)
  // ===================================================================

  "Call Analysis Agent": {
    nodes: [
      trigger(
        "Webhook: Sales call recording available from Gong",
        "webhook_app",
        {
          source: "Gong",
          event: "call.completed",
          howItStarts:
            "Gong fires a webhook when a sales call recording is processed and the transcript is available for analysis",
          setup:
            "Configure Gong webhooks to POST call completed events to this workflow URL. Ensure the payload includes call ID, participants, duration, and transcript access URL.",
          inputFields: [],
        },
      ),

      // 2. AI: Transcribe and extract key information
      aiNode(
        "2",
        "AI: Extract call transcript summary and key conversation details",
        240,
        "You are a sales call analyst. From the Gong call data, extract call_id, caller_name, caller_email, prospect_name, prospect_company, call_duration_minutes, and a transcript_summary (3-4 sentence overview of what was discussed). Return JSON.",
        "Extract call details from Gong webhook: {webhook_payload}",
        [
          { field: "call_id", as: "gong_call_id", type: "string" },
          { field: "caller_name", as: "rep_name", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          {
            field: "call_duration_minutes",
            as: "call_duration",
            type: "number",
          },
          {
            field: "transcript_summary",
            as: "transcript_summary",
            type: "string",
          },
        ],
      ),

      // 3. AI: Deep analysis of insights, sentiment, and objections
      aiNode(
        "3",
        "AI: Analyze call for sentiment, objections, next steps, and coaching opportunities",
        480,
        "You are an expert sales coach and conversation analyst. Analyze the call transcript and return: overall_sentiment (positive, neutral, negative), key_objections (array of objections raised), next_steps (array of agreed action items), competitor_mentions (any competitors discussed), buying_signals (positive indicators), risk_factors (concerns or red flags), talk_ratio_assessment (rep talked too much, balanced, prospect led), and call_quality_score (1-100). Return JSON.",
        "Analyze this sales call: Summary={transcript_summary}, Rep={rep_name}, Prospect={prospect_name} at {prospect_company}, Duration={call_duration} min",
        [
          { field: "overall_sentiment", as: "call_sentiment", type: "string" },
          { field: "key_objections", as: "key_objections", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
          { field: "buying_signals", as: "buying_signals", type: "string" },
          {
            field: "call_quality_score",
            as: "call_quality_score",
            type: "number",
          },
          { field: "talk_ratio_assessment", as: "talk_ratio", type: "string" },
        ],
      ),

      // 4. runCode: Score call quality
      n(
        "4",
        "runCode",
        "Compute composite call quality score and coaching priority",
        720,
        {
          codeDetails: {
            code: `output.quality_score = input.call_quality_score || 50;
output.needs_coaching = output.quality_score < 60;
output.coaching_priority = output.quality_score < 40 ? 'urgent' : output.quality_score < 60 ? 'recommended' : 'optional';
output.sentiment_positive = input.call_sentiment === 'positive';
output.has_next_steps = (input.next_steps || '').length > 5;
output.deal_risk = input.call_sentiment === 'negative' && !output.has_next_steps;`,
          },
        },
      ),

      // 5. HubSpot: Update deal with call insights
      n(
        "5",
        "hitApi",
        "HubSpot: Update deal record with call analysis and next steps",
        960,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"notes_last_contacted":"Call analysis ({call_duration}min): {transcript_summary}. Sentiment: {call_sentiment}. Quality: {call_quality_score}/100. Next steps: {next_steps}. Objections: {key_objections}."}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_deal_id", type: "string" },
              {
                name: "properties.notes_last_contacted",
                customName: "deal_notes_updated",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. Slack: Coaching note
      slackNode(
        "6",
        "Slack: Send call coaching note to rep's manager with quality score",
        1200,
        "Call Analysis for {rep_name}: Call with {prospect_name} ({prospect_company}), {call_duration} min. Quality: {call_quality_score}/100. Sentiment: {call_sentiment}. Talk ratio: {talk_ratio}. Coaching: {coaching_priority}. Objections: {key_objections}. Buying signals: {buying_signals}.",
      ),

      // 7. Email: Summary to rep
      emailNode(
        "7",
        "Email: Send call summary and action items to sales rep",
        1440,
        "{rep_email}",
        "Call Summary: {prospect_name} at {prospect_company} ({call_duration} min)",
        "<h2>Call Summary</h2><p><strong>Prospect:</strong> {prospect_name} at {prospect_company}</p><p><strong>Duration:</strong> {call_duration} minutes</p><p><strong>Sentiment:</strong> {call_sentiment}</p><h3>Key Takeaways</h3><p>{transcript_summary}</p><h3>Next Steps</h3><p>{next_steps}</p><h3>Objections to Address</h3><p>{key_objections}</p><h3>Buying Signals</h3><p>{buying_signals}</p>",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log call analysis metrics for coaching and reporting",
        1680,
        "CallAnalysis",
        '[["{ gong_call_id }","{ rep_name }","{ prospect_name }","{ prospect_company }","{ call_duration }","{ call_sentiment }","{ call_quality_score }","{ coaching_priority }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Analyzes Gong sales call recordings with AI for coaching and deal intelligence. When a call completes, AI extracts the transcript summary, then performs deep analysis of sentiment, objections, buying signals, and talk ratio to score call quality. HubSpot is updated with insights, managers get coaching notes via Slack, reps receive action item summaries by email, and all metrics are tracked in Google Sheets.",
  },

  // ===================================================================
  // 285. SUPPORT AGENT TRIAGE OR COPILOT — Pattern L (Usage/Expansion)
  // ===================================================================

  "Support Agent Triage Copilot": {
    nodes: [
      trigger("Webhook: New support ticket created in Zendesk", "webhook_app", {
        source: "Zendesk",
        event: "ticket.created",
        howItStarts:
          "Zendesk fires a webhook when a new support ticket is created, triggering AI classification and knowledge base search to suggest responses to the agent",
        setup:
          "Configure Zendesk webhook trigger for ticket.created events. Include ticket ID, subject, description, requester email, and priority in the payload.",
        inputFields: [],
      }),

      // 2. AI: Classify ticket and suggest response
      aiNode(
        "2",
        "AI: Classify support ticket category, urgency, and initial response suggestion",
        240,
        "You are a support ticket triage specialist. Classify the ticket_category (billing, technical, feature_request, bug_report, account, general), urgency (low, medium, high, critical), sentiment (positive, neutral, frustrated, angry), extract requester_email, requester_name, ticket_subject, ticket_description, and product_area (the specific product feature involved). Return JSON.",
        "Classify and triage this support ticket: {webhook_payload}",
        [
          { field: "ticket_category", as: "ticket_category", type: "string" },
          { field: "urgency", as: "ticket_urgency", type: "string" },
          { field: "sentiment", as: "customer_sentiment", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          { field: "product_area", as: "product_area", type: "string" },
        ],
      ),

      // 3. Pinecone: Search knowledge base for solutions
      n(
        "3",
        "hitApi",
        "Pinecone: Search knowledge base for relevant solution articles",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{ticket_embedding}" },
              { key: "topK", value: "5" },
              { key: "includeMetadata", value: "true" },
              { key: "namespace", value: "support-kb" },
            ],
            variables: [
              {
                name: "matches[0].metadata.title",
                customName: "kb_article_title",
                type: "string",
              },
              {
                name: "matches[0].metadata.content",
                customName: "kb_article_content",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "kb_match_score",
                type: "number",
              },
              {
                name: "matches[1].metadata.title",
                customName: "kb_article_2_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. runCode: Rank confidence and determine auto-send eligibility
      n(
        "4",
        "runCode",
        "Compute response confidence score and determine auto-send eligibility",
        720,
        {
          codeDetails: {
            code: `output.kb_match_score = input.kb_match_score || 0;
output.confidence = Math.round(output.kb_match_score * 100);
output.auto_sendable = output.confidence > 85 && input.customer_sentiment !== 'angry' && input.ticket_urgency !== 'critical';
output.suggested_response = input.kb_article_content || 'No matching article found. Manual response needed.';
output.kb_article = input.kb_article_title || 'No match';
output.needs_escalation = input.ticket_urgency === 'critical' || input.customer_sentiment === 'angry';
output.routing = input.ticket_category === 'billing' ? 'billing-team' : input.ticket_category === 'technical' ? 'engineering' : 'general-support';`,
          },
        },
      ),

      // 5. Condition: Is the response auto-sendable (high confidence)?
      n(
        "5",
        "condition",
        "Is the suggested response auto-sendable (confidence above 85%)?",
        960,
        {
          conditionData: [
            {
              variable: "auto_sendable",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Zendesk: Add internal note with suggested response
      n(
        "6",
        "hitApi",
        "Zendesk: Add internal note with AI-suggested response for agent review",
        1200,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"comment":{"body":"AI Copilot Suggestion (Confidence: {confidence}%):\\n\\nCategory: {ticket_category}\\nUrgency: {ticket_urgency}\\nKB Article: {kb_article}\\n\\nSuggested Response:\\n{suggested_response}\\n\\nRouting: {routing}","public":false},"tags":["{ticket_category}","ai-triaged","{routing}"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "updated_ticket_id",
                type: "string",
              },
              {
                name: "ticket.status",
                customName: "ticket_status",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Slack: Suggest response to agent
      slackNode(
        "7",
        "Slack: Suggest AI response to support agent with confidence score",
        1440,
        "Ticket #{updated_ticket_id}: {ticket_subject} from {requester_name}. Category: {ticket_category}. Urgency: {ticket_urgency}. Sentiment: {customer_sentiment}. AI confidence: {confidence}%. KB match: {kb_article}. Routing: {routing}. {needs_escalation ? 'ESCALATION NEEDED' : 'Standard handling'}",
      ),

      // 8. Google Sheets: Log effectiveness
      sheetsNode(
        "8",
        "Google Sheets: Log triage effectiveness and response accuracy metrics",
        1680,
        "SupportTriage",
        '[["{ updated_ticket_id }","{ ticket_category }","{ ticket_urgency }","{ customer_sentiment }","{ confidence }","{ auto_sendable }","{ kb_article }","{ routing }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Auto-sendable -> add as internal note
      e("5", "7", "output_2"), // Low confidence -> route to agent via Slack
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "AI-powered support ticket triage and copilot for Zendesk agents. When a ticket is created, AI classifies the category, urgency, and sentiment, Pinecone searches the knowledge base for solutions, and a confidence score determines if the response can be auto-suggested. High-confidence responses are added as internal notes in Zendesk, agents are notified via Slack, and triage effectiveness is tracked in Google Sheets.",
  },

  // ===================================================================
  // 286. CRM RESEARCH AGENT — Pattern K (Lead Scoring/Routing)
  // ===================================================================

  "CRM Research Agent": {
    nodes: [
      trigger(
        "Webhook: Research request submitted for prospect or account",
        "webhook_form",
        {
          source: "Form",
          event: "research.requested",
          howItStarts:
            "A sales rep submits a research request form with a target company domain or person name, triggering automated enrichment and dossier compilation",
          setup:
            "Create a research request form (or Slack command) that POSTs to this workflow URL. Include the target domain, contact name, and research purpose (prospecting, meeting prep, deal support).",
          inputFields: [],
        },
      ),

      // 2. Clearbit: Enrich company and person
      n(
        "2",
        "hitApi",
        "Clearbit: Enrich target company and key contacts from domain",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={target_domain}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              { name: "name", customName: "company_name", type: "string" },
              {
                name: "description",
                customName: "company_description",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "employee_count",
                type: "number",
              },
              {
                name: "metrics.estimatedAnnualRevenue",
                customName: "annual_revenue",
                type: "string",
              },
              {
                name: "category.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "category.subIndustry",
                customName: "sub_industry",
                type: "string",
              },
              { name: "tech", customName: "tech_stack", type: "array" },
              {
                name: "foundedYear",
                customName: "founded_year",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. LinkedIn API: Scan for key contacts
      n(
        "3",
        "hitApi",
        "LinkedIn: Search for key decision makers at target company",
        480,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.linkedin.com/v2/search?q=people&company={company_name}&keywords=VP+Director+Head&count=5",
            headers: [
              { key: "Authorization", value: "Bearer {linkedin_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "elements",
                customName: "linkedin_contacts",
                type: "array",
              },
              {
                name: "paging.total",
                customName: "contacts_found",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. AI: Compile research dossier
      aiNode(
        "4",
        "AI: Compile comprehensive research dossier from all enrichment sources",
        720,
        "You are a B2B research analyst. Compile a detailed dossier including: company_summary (3-4 sentences), icp_fit_score (1-100 based on size, industry, tech), key_contacts (formatted list of decision makers with titles), competitive_landscape (known competitors), pain_points (likely business challenges based on industry and tech stack), conversation_starters (3 personalized talking points), and recommended_approach (how to best engage this account). Return JSON.",
        "Compile dossier: Company={company_name}, Description={company_description}, Industry={industry}, Employees={employee_count}, Revenue={annual_revenue}, Tech={tech_stack}, Founded={founded_year}, LinkedIn contacts={linkedin_contacts}",
        [
          { field: "company_summary", as: "company_summary", type: "string" },
          { field: "icp_fit_score", as: "icp_fit_score", type: "number" },
          { field: "key_contacts", as: "key_contacts", type: "string" },
          { field: "pain_points", as: "pain_points", type: "string" },
          {
            field: "conversation_starters",
            as: "conversation_starters",
            type: "string",
          },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
        ],
      ),

      // 5. runCode: Format briefing document
      n(
        "5",
        "runCode",
        "Format research findings into structured briefing document",
        960,
        {
          codeDetails: {
            code: `output.briefing = '# Research Dossier: ' + input.company_name + '\\n\\n';
output.briefing += '## Company Summary\\n' + input.company_summary + '\\n\\n';
output.briefing += '## Key Metrics\\n- Industry: ' + input.industry + '\\n- Employees: ' + input.employee_count + '\\n- Revenue: ' + input.annual_revenue + '\\n- Founded: ' + input.founded_year + '\\n\\n';
output.briefing += '## Key Contacts\\n' + input.key_contacts + '\\n\\n';
output.briefing += '## Pain Points\\n' + input.pain_points + '\\n\\n';
output.briefing += '## Conversation Starters\\n' + input.conversation_starters + '\\n\\n';
output.briefing += '## Recommended Approach\\n' + input.recommended_approach;
output.icp_fit = input.icp_fit_score >= 70 ? 'Strong fit' : input.icp_fit_score >= 50 ? 'Moderate fit' : 'Weak fit';`,
          },
        },
      ),

      // 6. HubSpot: Update company notes
      n(
        "6",
        "hitApi",
        "HubSpot: Update company record with research findings and ICP score",
        1200,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/companies/{hubspot_company_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"description":"{company_summary}","industry":"{industry}","numberofemployees":"{employee_count}","annualrevenue":"{annual_revenue}","notes_last_contacted":"Research dossier generated. ICP fit: {icp_fit_score}/100. Pain points: {pain_points}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_company_id", type: "string" },
            ],
          },
        },
      ),

      // 7. Email: Deliver research brief
      emailNode(
        "7",
        "Email: Deliver completed research dossier to requesting sales rep",
        1440,
        "{requester_email}",
        "Research Dossier: {company_name} (ICP Fit: {icp_fit_score}/100)",
        "<h2>Research Dossier: {company_name}</h2><p><strong>ICP Fit:</strong> {icp_fit_score}/100 ({icp_fit})</p><h3>Company Summary</h3><p>{company_summary}</p><h3>Key Metrics</h3><p>Industry: {industry} | Employees: {employee_count} | Revenue: {annual_revenue}</p><h3>Key Contacts</h3><p>{key_contacts}</p><h3>Pain Points</h3><p>{pain_points}</p><h3>Conversation Starters</h3><p>{conversation_starters}</p><h3>Recommended Approach</h3><p>{recommended_approach}</p>",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log research request and dossier delivery",
        1680,
        "ResearchLog",
        '[["{ company_name }","{ industry }","{ employee_count }","{ annual_revenue }","{ icp_fit_score }","{ contacts_found }","{ requester_email }","delivered"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automated CRM research agent that compiles comprehensive prospect dossiers. When a rep submits a research request, Clearbit enriches the company, LinkedIn finds decision makers, and AI compiles a dossier with ICP scoring, pain points, and conversation starters. HubSpot is updated with findings, the brief is emailed to the requester, and all requests are logged to Google Sheets.",
  },

  // ===================================================================
  // 287. DATA ANALYSIS AGENT — Pattern L (Usage/Expansion)
  // ===================================================================

  "Data Analysis Agent": {
    nodes: [
      trigger(
        "Webhook: Data question submitted for automated analysis",
        "webhook_form",
        {
          source: "Form",
          event: "data.question_submitted",
          howItStarts:
            "A user submits a natural language data question via form or Slack command, triggering automated analysis against connected data sources",
          setup:
            "Create a data question form or Slack slash command that POSTs to this workflow URL. Include the question text, data source reference (spreadsheet ID), and preferred output format.",
          inputFields: [],
        },
      ),

      // 2. Google Sheets: Fetch data
      n(
        "2",
        "hitApi",
        "Google Sheets: Fetch raw data from the referenced spreadsheet",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/{sheet_range}",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "values", customName: "raw_data", type: "array" },
              { name: "range", customName: "data_range", type: "string" },
            ],
          },
        },
      ),

      // 3. AI: Analyze patterns in data
      aiNode(
        "3",
        "AI: Analyze data patterns and answer the submitted question",
        480,
        "You are a data analyst. Given raw spreadsheet data and a natural language question, analyze the data to answer the question. Provide: answer_summary (clear 2-3 sentence answer), key_findings (array of 3-5 insights), data_quality_notes (any issues with the data), visualization_suggestion (what chart would best represent the findings). Return JSON.",
        "Analyze this data to answer: Question={data_question}, Data columns={raw_data[0]}, Sample rows={raw_data[1..5]}, Total rows={raw_data.length}",
        [
          { field: "answer_summary", as: "answer_summary", type: "string" },
          { field: "key_findings", as: "key_findings", type: "string" },
          {
            field: "data_quality_notes",
            as: "data_quality_notes",
            type: "string",
          },
          {
            field: "visualization_suggestion",
            as: "viz_suggestion",
            type: "string",
          },
        ],
      ),

      // 4. runCode: Compute statistics
      n(
        "4",
        "runCode",
        "Compute statistical summaries and validate AI analysis",
        720,
        {
          codeDetails: {
            code: `const data = input.raw_data || [];
output.row_count = data.length - 1;
output.column_count = data[0]?.length || 0;
output.columns = data[0] || [];
output.has_numeric_data = data.length > 1 && data[1]?.some(v => !isNaN(parseFloat(v)));
output.analysis_complete = !!input.answer_summary;
output.actionable = input.key_findings && input.key_findings.length > 10;`,
          },
        },
      ),

      // 5. AI: Generate narrative summary
      aiNode(
        "5",
        "AI: Generate business-friendly narrative from statistical analysis",
        960,
        "You are a business intelligence narrator. Take the statistical analysis and key findings and produce a narrative_report (3-4 paragraph executive summary), action_items (3 specific recommended actions based on the data), and confidence_level (how confident in the analysis: high, medium, low). Return JSON.",
        "Generate narrative: Question={data_question}, Answer={answer_summary}, Findings={key_findings}, Stats: {row_count} rows x {column_count} columns, Quality={data_quality_notes}",
        [
          { field: "narrative_report", as: "narrative_report", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 6. Condition: Actionable insight found?
      n(
        "6",
        "condition",
        "Did the analysis produce actionable insights worth sharing?",
        1200,
        {
          conditionData: [
            {
              variable: "actionable",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Slack: Post findings
      slackNode(
        "7",
        "Slack: Post data analysis findings and action items to channel",
        1440,
        "Data Analysis Complete: {answer_summary}\n\nKey Findings: {key_findings}\n\nAction Items: {action_items}\n\nConfidence: {confidence_level} | Data: {row_count} rows analyzed | Viz suggestion: {viz_suggestion}",
      ),

      // 8. Google Sheets: Log analysis
      sheetsNode(
        "8",
        "Google Sheets: Log data analysis request and results",
        1680,
        "DataAnalysis",
        '[["{ data_question }","{ answer_summary }","{ row_count }","{ column_count }","{ confidence_level }","{ actionable }","complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Actionable -> post findings to Slack
      e("6", "8", "output_2"), // Not actionable -> just log
      e("7", "8"),
    ],
    summary:
      "AI-powered data analysis agent that answers natural language questions about spreadsheet data. When a question is submitted, the workflow fetches raw data from Google Sheets, AI analyzes patterns and computes answers, statistics are validated, and AI generates a business-friendly narrative with action items. Actionable insights are posted to Slack and all analyses are logged to Google Sheets.",
  },

  // ===================================================================
  // 288. WEBPAGE SCRAPE + SUMMARIZE — Pattern I (Content/Event)
  // ===================================================================

  "Webpage Scrape and Summarize": {
    nodes: [
      trigger(
        "Webhook: URL submitted for scraping and summarization",
        "webhook_form",
        {
          source: "Form",
          event: "url.submitted",
          howItStarts:
            "A user submits a URL via form or Slack command for automated scraping, summarization, and structured data extraction",
          setup:
            "Create a URL submission form or Slack slash command that POSTs to this workflow URL. Include the target URL, purpose (research, competitive, content), and desired output format.",
          inputFields: [],
        },
      ),

      // 2. API: Fetch page content
      n(
        "2",
        "hitApi",
        "Web Scraper: Fetch and extract content from the submitted URL",
        240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.scrapingbee.com/v1/",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "api_key", value: "{scraping_api_key}" },
              { key: "url", value: "{target_url}" },
              { key: "render_js", value: "false" },
              {
                key: "extract_rules",
                value:
                  '{"title":"h1","content":"article,main,.content","meta_description":"meta[name=description]@content"}',
              },
            ],
            variables: [
              { name: "title", customName: "page_title", type: "string" },
              { name: "content", customName: "page_content", type: "string" },
              {
                name: "meta_description",
                customName: "meta_description",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. AI: Summarize key points
      aiNode(
        "3",
        "AI: Summarize webpage content and extract key points",
        480,
        "You are a content analyst. Summarize the webpage content into: summary (3-4 sentence overview), key_points (5 bullet points of the most important information), topics (array of topic tags), content_type (article, product_page, landing_page, documentation, press_release), word_count estimate, and relevance_score (1-100 based on information density). Return JSON.",
        "Summarize this webpage: Title={page_title}, Description={meta_description}, Content={page_content}",
        [
          { field: "summary", as: "content_summary", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "topics", as: "topics", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "relevance_score", as: "relevance_score", type: "number" },
        ],
      ),

      // 4. runCode: Extract structured data
      n(
        "4",
        "runCode",
        "Extract structured data fields from page content",
        720,
        {
          codeDetails: {
            code: `output.page_title = input.page_title || 'Untitled';
output.content_summary = input.content_summary;
output.key_points = input.key_points;
output.content_type = input.content_type;
output.relevance_score = input.relevance_score || 0;
output.data_quality_ok = output.relevance_score >= 40;
output.topics = input.topics;
output.target_url = input.target_url;
output.extracted_at = new Date().toISOString();`,
          },
        },
      ),

      // 5. Condition: Data quality sufficient?
      n(
        "5",
        "condition",
        "Is the extracted content quality sufficient (relevance score above 40)?",
        960,
        {
          conditionData: [
            {
              variable: "data_quality_ok",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Airtable: Create record
      n(
        "6",
        "hitApi",
        "Airtable: Save structured webpage data to research database",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.airtable.com/v0/{base_id}/WebResearch",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"URL":"{target_url}","Title":"{page_title}","Summary":"{content_summary}","Key Points":"{key_points}","Content Type":"{content_type}","Topics":"{topics}","Relevance Score":{relevance_score},"Extracted At":"{extracted_at}"}',
              },
            ],
            variables: [
              { name: "id", customName: "airtable_record_id", type: "string" },
            ],
          },
        },
      ),

      // 7. Slack: Send summary
      slackNode(
        "7",
        "Slack: Send webpage summary and key points to requester",
        1440,
        "Webpage Summary: {page_title}\nURL: {target_url}\nType: {content_type} | Relevance: {relevance_score}/100\n\nSummary: {content_summary}\n\nKey Points: {key_points}\n\nTopics: {topics}",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log webpage scrape and analysis results",
        1680,
        "WebScrapes",
        '[["{ target_url }","{ page_title }","{ content_type }","{ relevance_score }","{ topics }","{ airtable_record_id }","{ extracted_at }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Quality OK -> save to Airtable
      e("5", "7", "output_2"), // Low quality -> just notify
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Scrapes and summarizes webpages on demand for research purposes. When a URL is submitted, the page is scraped, AI summarizes the content and extracts key points with topic tags, structured data is validated for quality, and high-quality results are saved to Airtable. The summary is shared via Slack and all scrapes are logged to Google Sheets.",
  },

  // ===================================================================
  // 289. COMPANY ENRICHMENT FROM WEBSITE — Pattern K (Lead Scoring)
  // ===================================================================

  "Company Enrichment from Website": {
    nodes: [
      trigger(
        "Webhook: Domain list submitted for batch company enrichment",
        "webhook_form",
        {
          source: "Form",
          event: "domains.submitted",
          howItStarts:
            "A user submits a list of company domains for automated enrichment, deduplication, and CRM import",
          setup:
            "Create a form or upload endpoint that POSTs domain lists to this workflow URL. Include the domain list (comma-separated or array) and purpose (prospecting, data cleanup, ABM).",
          inputFields: [],
        },
      ),

      // 2. Clearbit: Batch enrich companies
      n(
        "2",
        "hitApi",
        "Clearbit: Batch enrich company data from submitted domain list",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={domain}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              { name: "name", customName: "company_name", type: "string" },
              {
                name: "description",
                customName: "company_description",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "employee_count",
                type: "number",
              },
              {
                name: "metrics.estimatedAnnualRevenue",
                customName: "annual_revenue",
                type: "string",
              },
              {
                name: "category.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "location.country",
                customName: "country",
                type: "string",
              },
              { name: "tech", customName: "tech_stack", type: "array" },
            ],
          },
        },
      ),

      // 3. AI: Extract additional signals
      aiNode(
        "3",
        "AI: Extract additional company signals and classify ICP fit",
        480,
        "You are a B2B data analyst. From the enriched company data, determine: icp_fit (strong, moderate, weak), target_persona (role most likely to buy), buying_trigger (what might prompt them to buy now), industry_segment (specific niche within their industry), and growth_signal (hiring, funding, expansion, stable). Return JSON.",
        "Classify ICP fit: Company={company_name}, Description={company_description}, Industry={industry}, Employees={employee_count}, Revenue={annual_revenue}, Tech={tech_stack}, Country={country}",
        [
          { field: "icp_fit", as: "icp_fit", type: "string" },
          { field: "target_persona", as: "target_persona", type: "string" },
          { field: "buying_trigger", as: "buying_trigger", type: "string" },
          { field: "industry_segment", as: "industry_segment", type: "string" },
          { field: "growth_signal", as: "growth_signal", type: "string" },
        ],
      ),

      // 4. runCode: Dedupe and validate
      n(
        "4",
        "runCode",
        "Deduplicate against existing CRM records and validate data quality",
        720,
        {
          codeDetails: {
            code: `output.company_name = input.company_name;
output.domain = input.domain;
output.is_new = !input.existing_crm_match;
output.data_complete = !!(input.company_name && input.industry && input.employee_count);
output.icp_fit = input.icp_fit;
output.enrichment_quality = input.data_complete ? 'complete' : 'partial';
output.target_persona = input.target_persona;
output.growth_signal = input.growth_signal;`,
          },
        },
      ),

      // 5. Condition: New company not in CRM?
      n(
        "5",
        "condition",
        "Is this a new company not already in the CRM?",
        960,
        {
          conditionData: [
            {
              variable: "is_new",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. HubSpot: Create or update company
      n(
        "6",
        "hitApi",
        "HubSpot: Create new company record with enriched data",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/companies",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"name":"{company_name}","domain":"{domain}","industry":"{industry}","numberofemployees":"{employee_count}","annualrevenue":"{annual_revenue}","country":"{country}","description":"{company_description}","notes_last_contacted":"ICP fit: {icp_fit}. Target persona: {target_persona}. Growth signal: {growth_signal}. Buying trigger: {buying_trigger}."}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_company_id", type: "string" },
            ],
          },
        },
      ),

      // 7. Slack: Enrichment digest
      slackNode(
        "7",
        "Slack: Send enrichment digest with ICP fit classifications",
        1440,
        "Company Enriched: {company_name} ({domain}). Industry: {industry}. Employees: {employee_count}. Revenue: {annual_revenue}. ICP Fit: {icp_fit}. Target persona: {target_persona}. Growth: {growth_signal}. HubSpot: #{hubspot_company_id}",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log company enrichment results and ICP scoring",
        1680,
        "CompanyEnrichment",
        '[["{ domain }","{ company_name }","{ industry }","{ employee_count }","{ annual_revenue }","{ icp_fit }","{ target_persona }","{ is_new }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // New company -> create in HubSpot
      e("5", "7", "output_2"), // Existing -> just notify
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Batch enriches company data from website domains using Clearbit and AI. When domains are submitted, Clearbit provides firmographic data, AI classifies ICP fit and identifies buying triggers, records are deduplicated against CRM, and new companies are created in HubSpot with full enrichment. An enrichment digest is sent via Slack and results are logged to Google Sheets.",
  },

  // ===================================================================
  // 290. COMPETITOR RESEARCH DOSSIER — Pattern I (Content/Event)
  // ===================================================================

  "Competitor Research Dossier": {
    nodes: [
      trigger(
        "Webhook: Competitor name submitted for research dossier",
        "webhook_form",
        {
          source: "Form",
          event: "competitor.research_requested",
          howItStarts:
            "A user submits a competitor name or domain for automated research, compiling website analysis, news, reviews, and competitive positioning into a dossier",
          setup:
            "Create a competitor research request form that POSTs to this workflow URL. Include the competitor name, domain, and specific areas of interest (pricing, features, positioning, hiring).",
          inputFields: [],
        },
      ),

      // 2. API: Fetch website, news, and review data
      n(
        "2",
        "hitApi",
        "Clearbit: Fetch competitor company profile and public data",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={competitor_domain}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              { name: "name", customName: "competitor_name", type: "string" },
              {
                name: "description",
                customName: "competitor_description",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "competitor_employees",
                type: "number",
              },
              {
                name: "metrics.estimatedAnnualRevenue",
                customName: "competitor_revenue",
                type: "string",
              },
              {
                name: "category.industry",
                customName: "competitor_industry",
                type: "string",
              },
              { name: "tech", customName: "competitor_tech", type: "array" },
              {
                name: "metrics.raised",
                customName: "total_funding",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. AI: Compile competitive analysis
      aiNode(
        "3",
        "AI: Compile comprehensive competitive analysis from all data sources",
        480,
        "You are a competitive intelligence analyst. Compile a thorough competitive analysis including: positioning_summary (how they position themselves), strengths (5 key strengths), weaknesses (5 potential weaknesses), pricing_model (based on available info), target_market (who they sell to), differentiation (how they differ from us), threat_level (low, medium, high), and recommended_counter_strategy (how to compete against them). Return JSON.",
        "Analyze competitor: Name={competitor_name}, Description={competitor_description}, Industry={competitor_industry}, Employees={competitor_employees}, Revenue={competitor_revenue}, Tech={competitor_tech}, Funding={total_funding}",
        [
          {
            field: "positioning_summary",
            as: "positioning_summary",
            type: "string",
          },
          { field: "strengths", as: "competitor_strengths", type: "string" },
          { field: "weaknesses", as: "competitor_weaknesses", type: "string" },
          { field: "target_market", as: "target_market", type: "string" },
          { field: "threat_level", as: "threat_level", type: "string" },
          {
            field: "recommended_counter_strategy",
            as: "counter_strategy",
            type: "string",
          },
        ],
      ),

      // 4. runCode: Structure findings into dossier format
      n(
        "4",
        "runCode",
        "Structure competitive intelligence into formatted dossier document",
        720,
        {
          codeDetails: {
            code: `output.dossier_title = 'Competitive Intelligence: ' + input.competitor_name;
output.competitor_name = input.competitor_name;
output.threat_level = input.threat_level;
output.is_high_threat = input.threat_level === 'high';
output.positioning = input.positioning_summary;
output.strengths = input.competitor_strengths;
output.weaknesses = input.competitor_weaknesses;
output.counter_strategy = input.counter_strategy;
output.last_updated = new Date().toISOString();`,
          },
        },
      ),

      // 5. Google Docs: Create dossier document
      n(
        "5",
        "hitApi",
        "Google Docs: Create competitor research dossier document",
        960,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://docs.googleapis.com/v1/documents",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "title", value: "{dossier_title}" }],
            variables: [
              {
                name: "documentId",
                customName: "dossier_doc_id",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. Email: Deliver dossier
      emailNode(
        "6",
        "Email: Deliver competitor research dossier to requester",
        1200,
        "{requester_email}",
        "Competitor Dossier: {competitor_name} (Threat: {threat_level})",
        "<h2>Competitive Intelligence: {competitor_name}</h2><p><strong>Threat Level:</strong> {threat_level}</p><p><strong>Employees:</strong> {competitor_employees} | <strong>Revenue:</strong> {competitor_revenue} | <strong>Funding:</strong> ${total_funding}</p><h3>Positioning</h3><p>{positioning}</p><h3>Strengths</h3><p>{strengths}</p><h3>Weaknesses</h3><p>{weaknesses}</p><h3>Counter Strategy</h3><p>{counter_strategy}</p><p><a href='https://docs.google.com/document/d/{dossier_doc_id}'>Full Dossier in Google Docs</a></p>",
      ),

      // 7. Slack: Notify team
      slackNode(
        "7",
        "Slack: Notify strategy team about new competitor dossier",
        1440,
        "New competitor dossier: {competitor_name} (Threat: {threat_level}). {positioning}. Doc: https://docs.google.com/document/d/{dossier_doc_id}",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log competitor research for tracking and updates",
        1680,
        "CompetitorResearch",
        '[["{ competitor_name }","{ competitor_domain }","{ competitor_industry }","{ competitor_employees }","{ competitor_revenue }","{ threat_level }","{ dossier_doc_id }","{ last_updated }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Generates comprehensive competitor research dossiers on demand. When a competitor is submitted, Clearbit provides company data, AI compiles competitive analysis with strengths, weaknesses, and counter-strategies, and the findings are structured into a Google Docs dossier. The report is emailed to the requester, the strategy team is notified via Slack, and all research is tracked in Google Sheets.",
  },

  // ===================================================================
  // 291. LEAD ENRICHMENT AND ROUTING WITH AI WEB RESEARCH — Pattern K
  // ===================================================================

  "Lead Enrichment with AI Web Research": {
    nodes: [
      trigger(
        "Webhook: New lead captured requiring enrichment and routing",
        "webhook_app",
        {
          source: "HubSpot",
          event: "contact.creation",
          howItStarts:
            "HubSpot fires a webhook when a new lead is created, triggering automated enrichment with Clearbit, AI web research, ICP scoring, and intelligent routing",
          setup:
            "Configure HubSpot webhook for contact.creation events. Include the contact email, name, company, and source in the payload.",
          inputFields: [],
        },
      ),

      // 2. Clearbit: Enrich lead
      n(
        "2",
        "hitApi",
        "Clearbit: Enrich new lead with firmographic and technographic data",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://person.clearbit.com/v2/combined/find?email={lead_email}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              {
                name: "person.name.fullName",
                customName: "full_name",
                type: "string",
              },
              {
                name: "person.employment.title",
                customName: "job_title",
                type: "string",
              },
              {
                name: "person.employment.seniority",
                customName: "seniority",
                type: "string",
              },
              {
                name: "company.name",
                customName: "company_name",
                type: "string",
              },
              {
                name: "company.metrics.employees",
                customName: "company_size",
                type: "number",
              },
              {
                name: "company.metrics.estimatedAnnualRevenue",
                customName: "company_revenue",
                type: "string",
              },
              {
                name: "company.category.industry",
                customName: "company_industry",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. AI: Web research company context
      aiNode(
        "3",
        "AI: Research company context and determine outreach relevance",
        480,
        "You are a sales intelligence researcher. Based on the enriched lead data, research the company and determine: company_context (2-3 sentences on what the company does and recent activity), pain_point_hypothesis (likely business challenge they face), personalization_hook (something specific to reference in outreach), icp_score (1-100 based on size, industry, seniority fit), and lead_temperature (hot, warm, cold). Return JSON.",
        "Research lead: {full_name} ({job_title}, {seniority}) at {company_name}. Industry: {company_industry}. Size: {company_size} employees. Revenue: {company_revenue}",
        [
          { field: "company_context", as: "company_context", type: "string" },
          { field: "pain_point_hypothesis", as: "pain_point", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          { field: "icp_score", as: "icp_score", type: "number" },
          { field: "lead_temperature", as: "lead_temperature", type: "string" },
        ],
      ),

      // 4. runCode: Score ICP fit and route
      n(
        "4",
        "runCode",
        "Compute final lead score and determine routing based on ICP fit",
        720,
        {
          codeDetails: {
            code: `output.icp_score = input.icp_score || 0;
output.is_high_fit = output.icp_score >= 70;
output.lead_temperature = input.lead_temperature;
output.routing = output.icp_score >= 80 ? 'enterprise-ae' : output.icp_score >= 60 ? 'mid-market-sdr' : 'nurture-sequence';
output.priority = output.icp_score >= 80 ? 'P1' : output.icp_score >= 60 ? 'P2' : 'P3';
output.full_name = input.full_name;
output.company_name = input.company_name;
output.personalization_hook = input.personalization_hook;`,
          },
        },
      ),

      // 5. Condition: High ICP fit?
      n(
        "5",
        "condition",
        "Is the lead a high ICP fit (score 70 or above)?",
        960,
        {
          conditionData: [
            {
              variable: "is_high_fit",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. HubSpot: Create and route lead
      n(
        "6",
        "hitApi",
        "HubSpot: Update lead with enrichment data and route to assigned rep",
        1200,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"jobtitle":"{job_title}","company":"{company_name}","industry":"{company_industry}","hs_lead_status":"NEW","notes_last_contacted":"ICP Score: {icp_score}. Route: {routing}. Context: {company_context}. Hook: {personalization_hook}. Pain point: {pain_point}."}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
            ],
          },
        },
      ),

      // 7. Slack: Alert SDR
      slackNode(
        "7",
        "Slack: Alert assigned SDR about high-fit lead with personalization context",
        1440,
        "High-fit lead: {full_name} ({job_title}) at {company_name}. ICP: {icp_score}/100. Temperature: {lead_temperature}. Route: {routing}. Hook: {personalization_hook}. Pain point: {pain_point}. Priority: {priority}",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log lead enrichment and routing decision",
        1680,
        "LeadEnrichment",
        '[["{ full_name }","{ company_name }","{ job_title }","{ company_industry }","{ company_size }","{ icp_score }","{ lead_temperature }","{ routing }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // High fit -> update and route
      e("5", "8", "output_2"), // Low fit -> log for nurture
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Enriches and routes new leads using Clearbit data and AI web research. When a lead is created in HubSpot, Clearbit provides firmographic data, AI researches the company for context and personalization hooks, a composite ICP score determines routing (enterprise AE, mid-market SDR, or nurture), and high-fit leads trigger SDR alerts via Slack with full context.",
  },

  // ===================================================================
  // 292. GOOGLE MAPS LEADS TO NOTION — Pattern K (Lead Scoring)
  // ===================================================================

  "Google Maps Leads to Notion": {
    nodes: [
      trigger(
        "Webhook: Search criteria submitted for Google Maps lead discovery",
        "webhook_form",
        {
          source: "Form",
          event: "maps.search_submitted",
          howItStarts:
            "A user submits search criteria (business type, location, radius) to discover and capture leads from Google Maps into Notion for outreach",
          setup:
            "Create a lead discovery form that POSTs to this workflow URL. Include search query (e.g., 'restaurants in Austin TX'), radius, and maximum results desired.",
          inputFields: [],
        },
      ),

      // 2. Google Maps: Search for businesses
      n(
        "2",
        "hitApi",
        "Google Maps: Search for businesses matching the submitted criteria",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://maps.googleapis.com/maps/api/place/textsearch/json?query={search_query}&key={google_maps_key}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              { name: "results", customName: "places_results", type: "array" },
              { name: "status", customName: "search_status", type: "string" },
            ],
          },
        },
      ),

      // 3. AI: Extract and score business details
      aiNode(
        "3",
        "AI: Extract business details and score lead potential from Maps results",
        480,
        "You are a local business lead analyst. From the Google Maps search results, extract for each business: business_name, address, phone, website, rating, review_count, place_id, and compute a lead_score (1-100 based on ratings, review count indicating established business, and website availability). Also identify high_potential leads (score > 70). Return JSON with businesses array and total_found count.",
        "Extract and score leads from Maps results: {places_results}",
        [
          { field: "businesses", as: "scored_businesses", type: "string" },
          { field: "total_found", as: "total_leads_found", type: "number" },
          {
            field: "high_potential_count",
            as: "high_potential_count",
            type: "number",
          },
        ],
      ),

      // 4. runCode: Deduplicate against existing records
      n(
        "4",
        "runCode",
        "Deduplicate discovered leads against existing Notion database",
        720,
        {
          codeDetails: {
            code: `const businesses = JSON.parse(input.scored_businesses || '[]');
output.total_found = businesses.length;
output.new_leads = businesses;
output.high_potential = businesses.filter(b => b.lead_score > 70);
output.high_potential_count = output.high_potential.length;
output.has_high_potential = output.high_potential_count > 0;
output.search_query = input.search_query;`,
          },
        },
      ),

      // 5. Notion: Create entries for discovered leads
      n(
        "5",
        "hitApi",
        "Notion: Create database entries for each discovered business lead",
        960,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/pages",
            headers: [
              { key: "Authorization", value: "Bearer {notion_token}" },
              { key: "Notion-Version", value: "2022-06-28" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "parent",
                value: '{"database_id":"{notion_database_id}"}',
              },
              {
                key: "properties",
                value:
                  '{"Name":{"title":[{"text":{"content":"{business_name}"}}]},"Address":{"rich_text":[{"text":{"content":"{address}"}}]},"Phone":{"phone_number":"{phone}"},"Website":{"url":"{website}"},"Rating":{"number":{rating}},"Reviews":{"number":{review_count}},"Lead Score":{"number":{lead_score}},"Source":{"select":{"name":"Google Maps"}},"Status":{"select":{"name":"New"}}}',
              },
            ],
            variables: [
              { name: "id", customName: "notion_page_id", type: "string" },
            ],
          },
        },
      ),

      // 6. Condition: High potential leads found?
      n(
        "6",
        "condition",
        "Were any high-potential leads discovered (score above 70)?",
        1200,
        {
          conditionData: [
            {
              variable: "has_high_potential",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Email: Highlight list
      emailNode(
        "7",
        "Email: Send high-potential lead highlights to sales team",
        1440,
        "{requester_email}",
        "Google Maps Lead Discovery: {high_potential_count} High-Potential Leads Found",
        "<h2>Lead Discovery Results</h2><p><strong>Search:</strong> {search_query}</p><p><strong>Total Found:</strong> {total_found}</p><p><strong>High Potential (70+):</strong> {high_potential_count}</p><h3>Top Leads</h3><p>{high_potential}</p><p>All leads have been added to your Notion database for outreach tracking.</p>",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log lead discovery search and results",
        1680,
        "MapsLeads",
        '[["{ search_query }","{ total_found }","{ high_potential_count }","{ notion_page_id }","complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // High potential found -> email highlights
      e("6", "8", "output_2"), // No high potential -> just log
      e("7", "8"),
    ],
    summary:
      "Discovers and captures local business leads from Google Maps into Notion. When search criteria are submitted, Google Maps is queried, AI extracts and scores each business based on ratings and online presence, leads are deduplicated and created in a Notion database, and high-potential leads are highlighted via email to the sales team.",
  },

  // ===================================================================
  // 293. SLACK NEWS EXTRACTION TO AIRTABLE — Pattern I (Content/Event)
  // ===================================================================

  "Slack News Extraction to Airtable": {
    nodes: [
      trigger("Webhook: News article shared in Slack channel", "webhook_app", {
        source: "Slack",
        event: "message.posted",
        howItStarts:
          "A Slack webhook fires when a team member shares a news article link in a designated channel, triggering automated extraction, categorization, and archival",
        setup:
          "Configure Slack event subscriptions for message events in the designated news channel. Filter for messages containing URLs. Post webhook events to this workflow URL.",
        inputFields: [],
      }),

      // 2. AI: Extract article details
      aiNode(
        "2",
        "AI: Extract article details and metadata from shared Slack message",
        240,
        "You are a news analyst. From the Slack message containing a shared article, extract: article_url, article_title, publication_name, author, publish_date, summary (2-3 sentences), category (industry_news, competitor, product_update, market_trend, regulation, funding), relevance_to_us (high, medium, low), shared_by (Slack user who shared), and is_competitor_news (boolean). Return JSON.",
        "Extract article details from Slack message: {webhook_payload}",
        [
          { field: "article_url", as: "article_url", type: "string" },
          { field: "article_title", as: "article_title", type: "string" },
          { field: "publication_name", as: "publication", type: "string" },
          { field: "summary", as: "article_summary", type: "string" },
          { field: "category", as: "news_category", type: "string" },
          { field: "relevance_to_us", as: "relevance", type: "string" },
          { field: "is_competitor_news", as: "is_competitor", type: "boolean" },
          { field: "shared_by", as: "shared_by", type: "string" },
        ],
      ),

      // 3. runCode: Categorize and dedupe
      n(
        "3",
        "runCode",
        "Categorize article and check for duplicates against recent entries",
        480,
        {
          codeDetails: {
            code: `output.article_url = input.article_url;
output.article_title = input.article_title;
output.news_category = input.news_category;
output.is_competitor = input.is_competitor;
output.relevance = input.relevance;
output.is_duplicate = false;
output.should_alert = input.is_competitor || input.relevance === 'high';
output.tags = [input.news_category, input.relevance, input.is_competitor ? 'competitor' : 'general'].join(', ');`,
          },
        },
      ),

      // 4. Airtable: Create record
      n(
        "4",
        "hitApi",
        "Airtable: Create news article record in intelligence database",
        720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.airtable.com/v0/{base_id}/NewsIntelligence",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"Title":"{article_title}","URL":"{article_url}","Publication":"{publication}","Summary":"{article_summary}","Category":"{news_category}","Relevance":"{relevance}","Competitor News":{is_competitor},"Shared By":"{shared_by}","Tags":"{tags}"}',
              },
            ],
            variables: [
              { name: "id", customName: "airtable_record_id", type: "string" },
            ],
          },
        },
      ),

      // 5. Condition: Is this competitor news?
      n(
        "5",
        "condition",
        "Is this competitor news requiring strategy team attention?",
        960,
        {
          conditionData: [
            {
              variable: "is_competitor",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Slack: Alert strategy team
      slackNode(
        "6",
        "Slack: Alert strategy team about competitor news in #competitive-intel",
        1200,
        "Competitor News Alert: {article_title} ({publication}). {article_summary}. Relevance: {relevance}. Shared by {shared_by}. Full article: {article_url}",
      ),

      // 7. Email: Weekly digest compilation
      emailNode(
        "7",
        "Email: Add to weekly news digest for leadership",
        1440,
        "{digest_email}",
        "News Intel: {article_title} [{news_category}]",
        "<h3>{article_title}</h3><p><strong>Source:</strong> {publication} | <strong>Category:</strong> {news_category} | <strong>Relevance:</strong> {relevance}</p><p>{article_summary}</p><p><a href='{article_url}'>Read Full Article</a></p><p><em>Shared by {shared_by} | Archived in Airtable</em></p>",
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log news extraction for tracking and analytics",
        1680,
        "NewsLog",
        '[["{ article_title }","{ publication }","{ news_category }","{ relevance }","{ is_competitor }","{ shared_by }","{ airtable_record_id }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Competitor news -> alert strategy team
      e("5", "7", "output_2"), // General news -> add to digest
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically extracts, categorizes, and archives news articles shared in Slack. When a team member shares a news link, AI extracts article details and classifies relevance and category, the article is archived in Airtable, competitor news triggers strategy team alerts via Slack, and all articles are compiled into email digests and logged to Google Sheets.",
  },

  // ===================================================================
  // 294. DATASET JOIN AND NORMALIZATION — Pattern L (Usage/Expansion)
  // ===================================================================

  "Dataset Join and Normalization Workflow": {
    nodes: [
      trigger(
        "Webhook: Two datasets uploaded for joining and normalization",
        "webhook_form",
        {
          source: "Form",
          event: "datasets.uploaded",
          howItStarts:
            "A user uploads two datasets (CSV or JSON) for automated joining on a shared key field, fuzzy matching, and normalized output",
          setup:
            "Create a file upload form that POSTs to this workflow URL. Include both file references, the join key field name, and desired output format (CSV, JSON, Google Sheets).",
          inputFields: [],
        },
      ),

      // 2. runCode: Parse both datasets
      n(
        "2",
        "runCode",
        "Parse and validate both uploaded datasets into structured arrays",
        240,
        {
          codeDetails: {
            code: `const ds1 = JSON.parse(input.dataset_1 || '[]');
const ds2 = JSON.parse(input.dataset_2 || '[]');
output.dataset_1_rows = ds1.length;
output.dataset_2_rows = ds2.length;
output.dataset_1_columns = ds1[0] ? Object.keys(ds1[0]) : [];
output.dataset_2_columns = ds2[0] ? Object.keys(ds2[0]) : [];
output.join_key = input.join_key_field;
output.has_join_key_ds1 = output.dataset_1_columns.includes(input.join_key_field);
output.has_join_key_ds2 = output.dataset_2_columns.includes(input.join_key_field);
output.parse_success = output.dataset_1_rows > 0 && output.dataset_2_rows > 0;`,
          },
        },
      ),

      // 3. runCode: Join on key field
      n(
        "3",
        "runCode",
        "Perform inner join on the specified key field between datasets",
        480,
        {
          codeDetails: {
            code: `const ds1 = JSON.parse(input.dataset_1 || '[]');
const ds2 = JSON.parse(input.dataset_2 || '[]');
const key = input.join_key;
const ds2Map = {};
ds2.forEach(row => { ds2Map[row[key]] = row; });
output.joined_rows = ds1.filter(r => ds2Map[r[key]]).map(r => ({...r, ...ds2Map[r[key]]}));
output.unmatched_ds1 = ds1.filter(r => !ds2Map[r[key]]).length;
output.matched_count = output.joined_rows.length;
output.match_rate = ds1.length > 0 ? Math.round(output.matched_count / ds1.length * 100) : 0;`,
          },
        },
      ),

      // 4. AI: Handle fuzzy matches for unmatched rows
      aiNode(
        "4",
        "AI: Attempt fuzzy matching for unmatched rows using AI similarity",
        720,
        "You are a data matching specialist. Given unmatched rows from dataset 1 and available keys from dataset 2, attempt fuzzy matching. For each unmatched row, find the closest match by similarity (Levenshtein distance, phonetic similarity, abbreviation expansion). Return fuzzy_matches (array of {ds1_key, ds2_key, confidence}), total_fuzzy_matched (count), and average_confidence. Return JSON.",
        "Fuzzy match: Unmatched count={unmatched_ds1}, Join key={join_key}, Dataset 2 keys available for matching",
        [
          { field: "fuzzy_matches", as: "fuzzy_matches", type: "string" },
          {
            field: "total_fuzzy_matched",
            as: "fuzzy_matched_count",
            type: "number",
          },
          {
            field: "average_confidence",
            as: "fuzzy_confidence",
            type: "number",
          },
        ],
      ),

      // 5. runCode: Normalize schema
      n("5", "runCode", "Normalize and clean the merged dataset schema", 960, {
        codeDetails: {
          code: `output.final_rows = input.matched_count + (input.fuzzy_matched_count || 0);
output.total_columns = new Set([...input.dataset_1_columns, ...input.dataset_2_columns]).size;
output.match_rate = input.match_rate;
output.fuzzy_confidence = input.fuzzy_confidence || 0;
output.quality_score = Math.round((input.match_rate * 0.7) + (input.fuzzy_confidence * 0.3));
output.normalized = true;`,
        },
      }),

      // 6. Google Sheets: Output joined data
      sheetsNode(
        "6",
        "Google Sheets: Write joined and normalized dataset to output spreadsheet",
        1200,
        "JoinedData",
        '[["{ joined_rows }"]]',
      ),

      // 7. Slack: Confirm completion
      slackNode(
        "7",
        "Slack: Confirm dataset join completion with quality metrics",
        1440,
        "Dataset join complete: {matched_count} exact matches + {fuzzy_matched_count} fuzzy matches from {dataset_1_rows} x {dataset_2_rows} rows. Match rate: {match_rate}%. Quality score: {quality_score}/100. Output: Google Sheets.",
      ),

      // 8. Google Sheets: Quality report
      sheetsNode(
        "8",
        "Google Sheets: Log join quality report and metrics",
        1680,
        "JoinQuality",
        '[["{ dataset_1_rows }","{ dataset_2_rows }","{ matched_count }","{ fuzzy_matched_count }","{ match_rate }","{ quality_score }","{ total_columns }","complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Joins and normalizes two datasets with AI-powered fuzzy matching. When datasets are uploaded, both are parsed and validated, an inner join is performed on the specified key field, AI attempts fuzzy matching for unmatched rows, the schema is normalized, results are written to Google Sheets, and a quality report with match rates is shared via Slack.",
  },

  // ===================================================================
  // 295. JSON TO EXCEL CONVERSION — Pattern L (Usage/Expansion)
  // ===================================================================

  "JSON to Excel Conversion": {
    nodes: [
      trigger(
        "Webhook: JSON file submitted for Excel/Sheets conversion",
        "webhook_form",
        {
          source: "Form",
          event: "json.uploaded",
          howItStarts:
            "A user uploads a JSON file or pastes JSON data for automated conversion into a formatted Google Sheets spreadsheet",
          setup:
            "Create a JSON upload form that POSTs to this workflow URL. Include the JSON data or file reference, desired sheet name, and formatting preferences.",
          inputFields: [],
        },
      ),

      // 2. runCode: Parse JSON
      n("2", "runCode", "Parse JSON input and validate structure", 240, {
        codeDetails: {
          code: `try {
  const data = JSON.parse(input.json_data || '[]');
  output.parsed_data = Array.isArray(data) ? data : [data];
  output.row_count = output.parsed_data.length;
  output.is_array = Array.isArray(data);
  output.parse_success = true;
  output.sample_keys = output.parsed_data[0] ? Object.keys(output.parsed_data[0]) : [];
  output.column_count = output.sample_keys.length;
} catch(e) {
  output.parse_success = false;
  output.error = e.message;
}`,
        },
      }),

      // 3. runCode: Flatten nested structures
      n(
        "3",
        "runCode",
        "Flatten nested JSON objects into tabular rows and columns",
        480,
        {
          codeDetails: {
            code: `function flatten(obj, prefix = '') {
  const result = {};
  for (const key in obj) {
    const fullKey = prefix ? prefix + '.' + key : key;
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(result, flatten(obj[key], fullKey));
    } else {
      result[fullKey] = Array.isArray(obj[key]) ? JSON.stringify(obj[key]) : obj[key];
    }
  }
  return result;
}
const rows = input.parsed_data.map(r => flatten(r));
output.flat_rows = rows;
output.flat_columns = rows[0] ? Object.keys(rows[0]) : [];
output.flat_column_count = output.flat_columns.length;
output.has_nested = output.flat_column_count > input.column_count;`,
          },
        },
      ),

      // 4. Google Sheets: Create formatted output
      sheetsNode(
        "4",
        "Google Sheets: Create formatted spreadsheet with headers and data rows",
        720,
        "ConvertedData",
        '[["{ flat_columns }"],["{ flat_rows }"]]',
      ),

      // 5. Condition: Large dataset needing download link?
      n(
        "5",
        "condition",
        "Is the dataset large (more than 1000 rows) requiring a download link?",
        960,
        {
          conditionData: [
            {
              variable: "row_count",
              value: "1000",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Email: Send download link
      emailNode(
        "6",
        "Email: Send conversion result with Google Sheets download link",
        1200,
        "{requester_email}",
        "JSON Conversion Complete: {row_count} rows, {flat_column_count} columns",
        "<h2>JSON to Sheets Conversion Complete</h2><p><strong>Rows:</strong> {row_count}</p><p><strong>Columns:</strong> {flat_column_count} (expanded from {column_count} top-level fields)</p><p><strong>Nested flattening:</strong> {has_nested ? 'Yes, nested objects were flattened' : 'No nesting detected'}</p><p><a href='https://docs.google.com/spreadsheets/d/{spreadsheet_id}'>Open in Google Sheets</a></p><p>You can download as Excel (.xlsx) from the File menu in Google Sheets.</p>",
      ),

      // 7. Slack: Confirm conversion
      slackNode(
        "7",
        "Slack: Confirm JSON to Sheets conversion with file details",
        1440,
        "JSON conversion complete: {row_count} rows x {flat_column_count} columns written to Google Sheets. {has_nested ? 'Nested objects flattened.' : 'Flat structure.'} Spreadsheet ready for download.",
      ),

      // 8. Google Sheets: Log conversion
      sheetsNode(
        "8",
        "Google Sheets: Log conversion job details and metrics",
        1680,
        "ConversionLog",
        '[["{ row_count }","{ column_count }","{ flat_column_count }","{ has_nested }","{ parse_success }","complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Large dataset -> send download link
      e("5", "7", "output_2"), // Small dataset -> just confirm
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Converts JSON data to formatted Google Sheets spreadsheets. When JSON is uploaded, it is parsed and validated, nested structures are flattened into tabular format, data is written to Google Sheets with proper headers, large datasets get a download link emailed, and conversion is confirmed via Slack with file details and metrics logged.",
  },

  // ===================================================================
  // 296. AI AGENT CHAT OVER INTERNAL DOCS — Pattern L (Usage/Expansion)
  // ===================================================================

  "AI Agent Chat over Internal Docs": {
    nodes: [
      trigger(
        "Webhook: Question submitted to internal knowledge AI assistant",
        "webhook_form",
        {
          source: "Form",
          event: "question.submitted",
          howItStarts:
            "A team member submits a question to the AI knowledge assistant, which searches internal documents via vector embeddings to provide grounded answers with citations",
          setup:
            "Create a question input form or Slack command that POSTs to this workflow URL. Include the question text, user email, and optional topic filter.",
          inputFields: [],
        },
      ),

      // 2. Pinecone: Vector search for relevant document chunks
      n(
        "2",
        "hitApi",
        "Pinecone: Vector search for relevant document chunks matching the question",
        240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{question_embedding}" },
              { key: "topK", value: "8" },
              { key: "includeMetadata", value: "true" },
              { key: "namespace", value: "internal-docs" },
            ],
            variables: [
              {
                name: "matches[0].metadata.content",
                customName: "chunk_1_content",
                type: "string",
              },
              {
                name: "matches[0].metadata.source",
                customName: "chunk_1_source",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "chunk_1_score",
                type: "number",
              },
              {
                name: "matches[1].metadata.content",
                customName: "chunk_2_content",
                type: "string",
              },
              {
                name: "matches[1].metadata.source",
                customName: "chunk_2_source",
                type: "string",
              },
              {
                name: "matches[2].metadata.content",
                customName: "chunk_3_content",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. AI: Retrieve and rank relevant chunks
      aiNode(
        "3",
        "AI: Re-rank retrieved chunks by relevance to the specific question",
        480,
        "You are a retrieval quality analyst. Given the question and retrieved document chunks, re-rank them by relevance. Return: ranked_context (the most relevant chunks concatenated, max 3), source_documents (list of document names/URLs used), context_quality (high, medium, low based on how well chunks answer the question), and has_sufficient_context (boolean). Return JSON.",
        "Re-rank for question: {user_question}. Chunk 1 ({chunk_1_score}): {chunk_1_content} [Source: {chunk_1_source}]. Chunk 2: {chunk_2_content} [Source: {chunk_2_source}]. Chunk 3: {chunk_3_content}",
        [
          { field: "ranked_context", as: "ranked_context", type: "string" },
          { field: "source_documents", as: "source_documents", type: "string" },
          { field: "context_quality", as: "context_quality", type: "string" },
          {
            field: "has_sufficient_context",
            as: "has_context",
            type: "boolean",
          },
        ],
      ),

      // 4. AI: Generate grounded answer with citations
      aiNode(
        "4",
        "AI: Generate grounded answer with inline citations from retrieved documents",
        720,
        "You are an internal knowledge assistant. Answer the question using ONLY the provided context from internal documents. Include inline citations [Source: document name]. If the context does not contain enough information, say so clearly. Return: answer (the grounded answer with citations), confidence (1-100 how confident in the answer), follow_up_suggestion (related question they might want to ask), and needs_human (boolean: true if the answer is uncertain or the question is complex). Return JSON.",
        "Question: {user_question}. Context: {ranked_context}. Sources: {source_documents}",
        [
          { field: "answer", as: "ai_answer", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "number" },
          { field: "follow_up_suggestion", as: "follow_up", type: "string" },
          { field: "needs_human", as: "needs_human", type: "boolean" },
        ],
      ),

      // 5. Condition: Confidence above threshold?
      n(
        "5",
        "condition",
        "Is the AI answer confidence above 70% (reliable enough to share)?",
        960,
        {
          conditionData: [
            {
              variable: "answer_confidence",
              value: "70",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. sendMessage: Reply with cited answer
      n(
        "6",
        "sendMessage",
        "Send AI-generated answer with citations to the questioner",
        1200,
        {
          sendMessageDetails: {
            to: "{user_email}",
            message:
              "Answer to your question: {user_question}\n\n{ai_answer}\n\nSources: {source_documents}\n\nConfidence: {answer_confidence}%\n\nFollow-up: {follow_up}",
          },
        },
      ),

      // 7. Slack: Escalate low-confidence questions
      slackNode(
        "7",
        "Slack: Escalate low-confidence question to subject matter expert",
        1440,
        'Knowledge AI needs help: {user_email} asked: "{user_question}". AI confidence: {answer_confidence}%. Context quality: {context_quality}. Best answer attempted: {ai_answer}. Please review and provide a human response.',
      ),

      // 8. Google Sheets: Log effectiveness
      sheetsNode(
        "8",
        "Google Sheets: Log question, answer, and confidence for quality tracking",
        1680,
        "KnowledgeAI",
        '[["{ user_question }","{ ai_answer }","{ answer_confidence }","{ context_quality }","{ source_documents }","{ needs_human }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // High confidence -> send answer
      e("5", "7", "output_2"), // Low confidence -> escalate to human
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "RAG-powered AI assistant that answers questions using internal documents. When a question is submitted, Pinecone performs vector search for relevant document chunks, AI re-ranks by relevance, then generates a grounded answer with inline citations. High-confidence answers are sent directly to the user, while low-confidence questions are escalated to subject matter experts via Slack.",
  },

  // ===================================================================
  // 297. FAQ OR REQUEST MANAGER FROM SLACK WITH AI — Pattern L
  // ===================================================================

  "FAQ and Request Manager from Slack": {
    nodes: [
      trigger(
        "Webhook: Question or request posted in Slack support channel",
        "webhook_app",
        {
          source: "Slack",
          event: "message.posted",
          howItStarts:
            "A Slack webhook fires when someone posts a question or request in the designated support or help channel, triggering AI classification and automated response or ticket creation",
          setup:
            "Configure Slack event subscriptions for the help/support channel. Filter for user messages (not bot messages). Post events to this workflow URL.",
          inputFields: [],
        },
      ),

      // 2. AI: Classify FAQ vs request
      aiNode(
        "2",
        "AI: Classify message as FAQ question, service request, or general inquiry",
        240,
        "You are a support triage specialist. Classify the Slack message as: message_type (faq, service_request, feedback, general), extract the user_name, user_email (from Slack profile), question_text (cleaned version of the question), topic (IT, HR, finance, product, engineering, facilities, other), urgency (low, medium, high), and faq_keywords (key terms for knowledge base search). Return JSON.",
        "Classify this Slack support message: {webhook_payload}",
        [
          { field: "message_type", as: "message_type", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "question_text", as: "question_text", type: "string" },
          { field: "topic", as: "topic", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "faq_keywords", as: "faq_keywords", type: "string" },
        ],
      ),

      // 3. Pinecone: Search knowledge base
      n(
        "3",
        "hitApi",
        "Pinecone: Search FAQ knowledge base for matching answers",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{question_embedding}" },
              { key: "topK", value: "3" },
              { key: "includeMetadata", value: "true" },
              { key: "namespace", value: "faq-kb" },
            ],
            variables: [
              {
                name: "matches[0].metadata.answer",
                customName: "faq_answer",
                type: "string",
              },
              {
                name: "matches[0].metadata.question",
                customName: "faq_question",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "faq_match_score",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. AI: Generate contextual answer
      aiNode(
        "4",
        "AI: Generate contextual answer from FAQ match or compose helpful response",
        720,
        "You are a helpful internal support AI. If the FAQ match score is high (>0.85), use the FAQ answer as the basis for your response. Otherwise, provide a helpful response based on common knowledge about the topic. Return: response_text (the answer to post in Slack thread), is_faq_match (boolean: true if using FAQ), confidence (1-100), and needs_ticket (boolean: true if this requires a service request or human follow-up). Return JSON.",
        "Generate response: Question={question_text}, Topic={topic}, FAQ match ({faq_match_score}): Q={faq_question}, A={faq_answer}",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "is_faq_match", as: "is_faq_match", type: "boolean" },
          { field: "confidence", as: "response_confidence", type: "number" },
          { field: "needs_ticket", as: "needs_ticket", type: "boolean" },
        ],
      ),

      // 5. Condition: Is this a clear FAQ match?
      n(
        "5",
        "condition",
        "Is this a confident FAQ match that can be answered automatically?",
        960,
        {
          conditionData: [
            {
              variable: "is_faq_match",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
            {
              variable: "response_confidence",
              value: "75",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Slack: Reply in thread with FAQ answer
      slackNode(
        "6",
        "Slack: Reply in thread with AI-generated FAQ answer",
        1200,
        "{response_text}\n\n_This answer was generated by AI from our knowledge base (confidence: {response_confidence}%). If this doesn't fully answer your question, please react with :x: and a human will follow up._",
      ),

      // 7. Jira: Create service request ticket
      n(
        "7",
        "hitApi",
        "Jira: Create service request ticket for non-FAQ requests",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"SR"},"issuetype":{"name":"Service Request"},"summary":"[{topic}] {question_text}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Submitted by {user_name} via Slack. Topic: {topic}. Urgency: {urgency}. Original message: {question_text}"}]}]},"priority":{"name":"{urgency}"}}',
              },
            ],
            variables: [
              { name: "key", customName: "jira_ticket_key", type: "string" },
            ],
          },
        },
      ),

      // 8. Google Sheets: Log
      sheetsNode(
        "8",
        "Google Sheets: Log FAQ/request interaction for analytics",
        1680,
        "SlackSupport",
        '[["{ user_name }","{ question_text }","{ message_type }","{ topic }","{ is_faq_match }","{ response_confidence }","{ needs_ticket }","{ jira_ticket_key }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // FAQ match -> reply in thread
      e("5", "7", "output_2"), // Not FAQ / needs ticket -> create Jira
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "AI-powered FAQ and service request manager for Slack. When someone posts a question in the help channel, AI classifies it as FAQ or service request, Pinecone searches the knowledge base for matching answers, and AI generates a contextual response. FAQ matches get an automated Slack thread reply, while service requests create Jira tickets, and all interactions are logged for analytics.",
  },

  // ===================================================================
  // 298. RESEARCH ANY COMPANY OR PERSON FOR OUTREACH — Pattern K
  // ===================================================================

  "Research Company or Person for Outreach": {
    nodes: [
      trigger(
        "Webhook: Research target submitted for outreach preparation",
        "webhook_form",
        {
          source: "Form",
          event: "outreach.research_requested",
          howItStarts:
            "A sales rep submits a target company or person for automated research and personalized outreach brief generation",
          setup:
            "Create an outreach research form that POSTs to this workflow URL. Include the target (company domain or person LinkedIn URL), outreach purpose (cold, warm, follow-up), and product to pitch.",
          inputFields: [],
        },
      ),

      // 2. Clearbit: Enrich target
      n(
        "2",
        "hitApi",
        "Clearbit: Enrich target person and company from submitted details",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://person.clearbit.com/v2/combined/find?email={target_email}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              {
                name: "person.name.fullName",
                customName: "target_name",
                type: "string",
              },
              {
                name: "person.employment.title",
                customName: "target_title",
                type: "string",
              },
              {
                name: "person.employment.seniority",
                customName: "target_seniority",
                type: "string",
              },
              { name: "person.bio", customName: "target_bio", type: "string" },
              {
                name: "company.name",
                customName: "target_company",
                type: "string",
              },
              {
                name: "company.description",
                customName: "company_description",
                type: "string",
              },
              {
                name: "company.metrics.employees",
                customName: "company_employees",
                type: "number",
              },
              {
                name: "company.category.industry",
                customName: "company_industry",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. LinkedIn: Fetch professional profile context
      n(
        "3",
        "hitApi",
        "LinkedIn: Fetch professional profile and recent activity for target",
        480,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.linkedin.com/v2/people/(id:{linkedin_id})?projection=(firstName,lastName,headline,positions,summary)",
            headers: [
              { key: "Authorization", value: "Bearer {linkedin_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "headline",
                customName: "linkedin_headline",
                type: "string",
              },
              {
                name: "summary",
                customName: "linkedin_summary",
                type: "string",
              },
              {
                name: "positions.values[0].title",
                customName: "current_position",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. AI: Web research and compile insights
      aiNode(
        "4",
        "AI: Research target for outreach-relevant insights and personalization angles",
        720,
        "You are an outreach research specialist. Based on the enriched person and company data, compile: person_summary (3 sentences about the individual and their role), company_fit (why this company might need our product), pain_points (3 likely challenges based on role and industry), personalization_angles (3 specific things to reference in outreach based on their background, recent activity, or company news), icebreaker (a natural conversation opener), and outreach_channel_recommendation (email, LinkedIn, phone, or multi-touch). Return JSON.",
        "Research for outreach: Person={target_name} ({target_title}, {target_seniority}), Bio={target_bio}, Company={target_company} ({company_industry}, {company_employees} employees), LinkedIn headline={linkedin_headline}, LinkedIn summary={linkedin_summary}, Current role={current_position}, Our product to pitch={product_to_pitch}",
        [
          { field: "person_summary", as: "person_summary", type: "string" },
          { field: "company_fit", as: "company_fit", type: "string" },
          { field: "pain_points", as: "pain_points", type: "string" },
          {
            field: "personalization_angles",
            as: "personalization_angles",
            type: "string",
          },
          { field: "icebreaker", as: "icebreaker", type: "string" },
          {
            field: "outreach_channel_recommendation",
            as: "channel_rec",
            type: "string",
          },
        ],
      ),

      // 5. AI: Compile outreach brief with draft messaging
      aiNode(
        "5",
        "AI: Generate personalized outreach brief with draft email and LinkedIn message",
        960,
        "You are a sales outreach copywriter. Using the research insights, create: email_subject (compelling subject line), email_draft (3-4 paragraph cold email that references personalization angles), linkedin_message (short LinkedIn connection request note under 300 chars), call_script_opener (15-second phone opener), follow_up_cadence (recommended timing for multi-touch sequence), and enough_data (boolean: true if sufficient information for quality outreach). Return JSON.",
        "Create outreach for: {target_name} at {target_company}. Icebreaker: {icebreaker}. Pain points: {pain_points}. Personalization: {personalization_angles}. Company fit: {company_fit}. Channel: {channel_rec}. Product: {product_to_pitch}",
        [
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_draft", as: "email_draft", type: "string" },
          { field: "linkedin_message", as: "linkedin_message", type: "string" },
          { field: "call_script_opener", as: "call_opener", type: "string" },
          { field: "enough_data", as: "enough_data", type: "boolean" },
        ],
      ),

      // 6. Condition: Enough data for quality outreach?
      n(
        "6",
        "condition",
        "Is there enough data to generate quality personalized outreach?",
        1200,
        {
          conditionData: [
            {
              variable: "enough_data",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Google Docs: Create outreach dossier
      n(
        "7",
        "hitApi",
        "Google Docs: Create outreach research dossier with draft messaging",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://docs.googleapis.com/v1/documents",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "title",
                value: "Outreach Brief: {target_name} at {target_company}",
              },
            ],
            variables: [
              {
                name: "documentId",
                customName: "dossier_doc_id",
                type: "string",
              },
            ],
          },
        },
      ),

      // 8. Email: Deliver outreach brief
      emailNode(
        "8",
        "Email: Deliver personalized outreach brief with all draft messaging",
        1680,
        "{requester_email}",
        "Outreach Brief Ready: {target_name} at {target_company}",
        "<h2>Outreach Brief: {target_name}</h2><p><strong>{target_title}</strong> at {target_company} ({company_industry})</p><h3>Person Summary</h3><p>{person_summary}</p><h3>Company Fit</h3><p>{company_fit}</p><h3>Pain Points</h3><p>{pain_points}</p><h3>Personalization Angles</h3><p>{personalization_angles}</p><h3>Draft Email</h3><p><strong>Subject:</strong> {email_subject}</p><p>{email_draft}</p><h3>LinkedIn Message</h3><p>{linkedin_message}</p><h3>Call Opener</h3><p>{call_opener}</p><p>Recommended channel: {channel_rec}</p><p><a href='https://docs.google.com/document/d/{dossier_doc_id}'>Full Dossier</a></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Enough data -> create dossier
      e("6", "8", "output_2"), // Insufficient data -> deliver what we have
      e("7", "8"),
    ],
    summary:
      "Researches any company or person and generates personalized outreach materials. When a target is submitted, Clearbit enriches person and company data, LinkedIn provides professional context, AI identifies pain points and personalization angles, then generates draft email, LinkedIn message, and call scripts. The complete outreach brief is saved to Google Docs and delivered to the requesting rep.",
  },
};
